import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import HomePage from './pages/HomePage';
import PiecePage from './pages/PiecePage';
import TunerPage from './pages/TunerPage';
import FavoritesPage from './pages/FavoritesPage';
import PracticePage from './pages/PracticePage';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import axios from 'axios';
import './App.css';

// Sidebar wrapper to access location
const SidebarWrapper = ({ menuOpen, toggleMenu, user, setMenuOpen, currentChatId, onChatSelect, onNewChat }) => {
  const location = useLocation();
  const isPracticePage = location.pathname === '/practice';
  
  return (
    <Sidebar 
      menuOpen={menuOpen} 
      toggleMenu={toggleMenu} 
      user={user} 
      setMenuOpen={setMenuOpen}
      currentChatId={currentChatId}
      onChatSelect={onChatSelect}
      onNewChat={onNewChat}
      isPracticePage={isPracticePage}
    />
  );
};

function App() {
  const [selectedPiece, setSelectedPiece] = useState(null);  // To store the selected piece
  const [results, setResults] = useState([]);  // To store search results
  const [favorites, setFavorites] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  // Chat state
  const [currentChatId, setCurrentChatId] = useState(null);
  const API_BASE = process.env.REACT_APP_API_BASE || (window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://studyvarious.com/api');
  const apiUrl = `${API_BASE}/search`;

  // Helper to get current user from localStorage
  const getCurrentUser = () => {
    try {
      const raw = localStorage.getItem('sv_user');
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  };

  const loadFavorites = async () => {
    const user = getCurrentUser();
    if (!user) {
      setFavorites([]);
      return;
    }
    try {
      const res = await axios.get(`${API_BASE}/favorites`, { params: { user_id: user.id } });
      setFavorites(res.data || []);
    } catch (e) {
      console.error('Failed to load favorites', e);
    }
  };

  // Listen for user changes (login/logout) and reload favorites
  useEffect(() => {
    const onUserChanged = () => loadFavorites();
    window.addEventListener('user-changed', onUserChanged);
    // initial load
    loadFavorites();
    return () => window.removeEventListener('user-changed', onUserChanged);
  }, []);

  // Hydrate user from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem('sv_user');
      if (stored) {
        const u = JSON.parse(stored);
        // ensure avatar is absolute
        if (u && u.id && (!u.picture || u.picture.startsWith('/'))) {
          u.picture = `${API_BASE}${u.picture || ('/user/avatar/' + u.id)}`;
        }
        setUser(u);
      }
    } catch {}
    // Listen for external requests to open the auth modal
    const onOpenAuth = () => {
      // Since auth modal is in Header, we need to dispatch or pass a way to open it
      // For now, since we're moving, perhaps keep it in Header, but pass setShowAuthOptions
      // Wait, Header still has showAuthOptions, so perhaps keep user in App, but showAuthOptions in Header.
      // To simplify, perhaps keep user in Header for now, but since user is needed for Sidebar, move it.
      // Actually, for Sidebar, user is optional, but for auth, keep in Header.
      // But to move Sidebar to App, and user is in App now.
    };
    window.addEventListener('open-auth', onOpenAuth);
    return () => window.removeEventListener('open-auth', onOpenAuth);
  }, []);

  // Sync sidebar state with body class for layout shift
  useEffect(() => {
    if (menuOpen) {
      document.body.classList.add('sidebar-open');
    } else {
      document.body.classList.remove('sidebar-open');
    }
    return () => document.body.classList.remove('sidebar-open');
  }, [menuOpen]);

  const isFavorited = (piece) => {
    return favorites.some((f) => f.id === piece.id);
  };

  const addFavorite = async (piece) => {
    const user = getCurrentUser();
    if (!user) {
      // open auth modal
      window.dispatchEvent(new CustomEvent('open-auth'));
      return;
    }
    // optimistic update
    setFavorites((s) => [...s, piece]);
    try {
      await axios.post(`${API_BASE}/favorites`, { user_id: user.id, piece });
    } catch (e) {
      console.error('Failed to add favorite', e);
      // revert
      setFavorites((s) => s.filter((p) => p.id !== piece.id));
    }
  };

  const removeFavorite = async (piece) => {
    const user = getCurrentUser();
    if (!user) {
      window.dispatchEvent(new CustomEvent('open-auth'));
      return;
    }
    setFavorites((s) => s.filter((p) => p.id !== piece.id));
    try {
      await axios.post(`${API_BASE}/favorites/remove`, { user_id: user.id, id: piece.id });
    } catch (e) {
      console.error('Failed to remove favorite', e);
      // revert by re-adding
      setFavorites((s) => [...s, piece]);
    }
  };

  // Chat handlers
  const handleChatSelect = (chatId) => {
    setCurrentChatId(chatId);
  };

  const handleNewChat = (chatId) => {
    setCurrentChatId(chatId);
  };

  useEffect(() => {
    console.log('selectedPiece ', selectedPiece);
  }, [selectedPiece]);
  const handleSearch = async (query) => {
    try {
      const response = await axios.post(apiUrl, { query });
      setResults(response.data);  // Update results with fetched data
    } catch (error) {
      console.error("Error performing search:", error);
    }
  };

  // Check for query param on load and search if present
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.get('query');
    if (query) {
      handleSearch(query);
    }
  }, []);

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <Router>
        <div className="app-shell">
          <Header onSearch={handleSearch} user={user} setUser={setUser} setMenuOpen={setMenuOpen} />
          <SidebarWrapper 
            menuOpen={menuOpen} 
            toggleMenu={() => setMenuOpen(!menuOpen)} 
            user={user} 
            setMenuOpen={setMenuOpen}
            currentChatId={currentChatId}
            onChatSelect={handleChatSelect}
            onNewChat={handleNewChat}
          />
          <main className="app-content">
            <Routes>
              <Route
                path="/"
                element={<HomePage setSelectedPiece={setSelectedPiece} results={results} favorites={favorites} addFavorite={addFavorite} removeFavorite={removeFavorite} isFavorited={isFavorited} />}
              />
              <Route
                path="/piece"
                element={<PiecePage selectedPiece={selectedPiece} addFavorite={addFavorite} removeFavorite={removeFavorite} isFavorited={isFavorited} />}
              />
              <Route
                path="/tuner"
                element={<TunerPage />}
              />
              <Route
                path="/practice"
                element={<PracticePage setSelectedPiece={setSelectedPiece} user={user} currentChatId={currentChatId} onChatSelect={handleChatSelect} onNewChat={handleNewChat} />}
              />
              <Route
                path="/favorites"
                element={<FavoritesPage setSelectedPiece={setSelectedPiece} favorites={favorites} removeFavorite={removeFavorite} />}
              />
            </Routes>
          </main>
        </div>
      </Router>
    </GoogleOAuthProvider>
  );
}

export default App;
